import { useRef, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Paperclip, UploadSimple, Trash, DownloadSimple, FilePdf, Star } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";
import { ConfirmDialog } from "./shared";

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function AttachThumb({ fileId, alt, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let objUrl = null;
    api.get(`/attachment-file/${fileId}`, { responseType: "blob" })
      .then((resp) => {
        if (!active) return;
        objUrl = URL.createObjectURL(resp.data);
        setUrl(objUrl);
      })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [fileId]);
  return (
    <button onClick={onClick} className="h-10 w-10 shrink-0 rounded-sm overflow-hidden border border-border bg-secondary/40 flex items-center justify-center" title={alt} data-testid="attachment-thumb">
      {url
        ? <img src={url} alt={alt} className="h-full w-full object-cover" />
        : <div className="h-4 w-4 border-2 border-muted-foreground/40 border-t-transparent rounded-full animate-spin" />}
    </button>
  );
}

export function AttachmentsPane({ entityType, entityId, isNew, label, allowPrimary = false, primaryId = null, onPrimaryChange, readOnly = false }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const key = ["attachments", entityType, entityId];

  const { data: files = [] } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/attachments/${entityType}/${entityId}`)).data,
    enabled: !isNew && !!entityId,
  });

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post(`/attachments/${entityType}/${entityId}`, fd)).data;
  };

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/attachments/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("File removed"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
    onSettled: () => setToDelete(null),
  });

  const primary = useMutation({
    mutationFn: async (id) => (await api.post(`/attachment-primary/${id}`)).data,
    onSuccess: (r) => { onPrimaryChange?.(r.primary_image_id); toast.success(r.primary_image_id ? "Set as main image" : "Main image cleared"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const handleFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    const valid = [];
    for (const f of list) {
      if (!ALLOWED.includes(f.type)) { toast.error(`"${f.name}": only PDF, JPG and PNG files are allowed.`); continue; }
      if (f.size > MAX_SIZE) { toast.error(`"${f.name}" is larger than the 5 MB limit.`); continue; }
      valid.push(f);
    }
    if (!valid.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of valid) {
      try { await uploadOne(f); ok += 1; }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Could not upload "${f.name}"`); }
    }
    setUploading(false);
    if (ok) { qc.invalidateQueries({ queryKey: key }); toast.success(ok === 1 ? "File uploaded" : `${ok} files uploaded`); }
  };

  const onPick = (e) => { const fl = e.target.files; e.target.value = ""; handleFiles(fl); };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (isNew || uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  const openFile = async (f, download = false) => {
    try {
      const resp = await api.get(`/attachment-file/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = f.original_filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open the file.");
    }
  };

  if (isNew) return <p className="text-sm text-muted-foreground">Save the document to attach files.</p>;

  return (
    <div className="space-y-3">
      <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        className="hidden" onChange={onPick} data-testid={`${entityType}-attach-input`} />

      {!readOnly && <div
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
        onDrop={onDrop}
        className={`cursor-pointer rounded-sm border-2 border-dashed px-3 py-5 text-center transition-colors ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/60 hover:bg-secondary/40"}`}
        data-testid={`${entityType}-attach-dropzone`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Uploading…
          </div>
        ) : (
          <>
            <UploadSimple size={20} className="mx-auto text-primary mb-1" />
            <p className="text-sm font-medium">{dragging ? "Drop files to upload" : (label || "Upload File")}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Drag &amp; drop or click · PDF, JPG, PNG · up to 5 MB each</p>
          </>
        )}
      </div>}

      <div className="space-y-2 max-h-72 overflow-y-auto" data-testid={`${entityType}-attach-list`}>
        {files.length === 0 && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Paperclip size={14} /> No files attached yet.</p>}
        {files.map((f) => {
          const isImage = (f.content_type || "").startsWith("image/");
          return (
            <div key={f.id} className="border border-border rounded-sm p-2.5 flex items-start gap-2" data-testid="attachment-item">
              {isImage
                ? <AttachThumb fileId={f.id} alt={f.original_filename} onClick={() => openFile(f)} />
                : <div className="mt-0.5 text-primary shrink-0"><FilePdf size={18} /></div>}
              <div className="min-w-0 flex-1">
                <button onClick={() => openFile(f)} className="text-sm text-left truncate w-full hover:underline hover:text-primary" data-testid="attachment-open" title={f.original_filename}>
                  {f.original_filename}
                </button>
                <p className="text-[11px] text-muted-foreground mt-0.5">{fmtSize(f.size)} · {f.uploaded_by} · {(f.created_at || "").slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {allowPrimary && isImage && !readOnly && (
                  <button onClick={() => primary.mutate(f.id)} className={`p-1 ${primaryId === f.id ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`} data-testid="attachment-set-primary" title={primaryId === f.id ? "Main image (click to clear)" : "Set as main image"}>
                    <Star size={15} weight={primaryId === f.id ? "fill" : "regular"} />
                  </button>
                )}
                <button onClick={() => openFile(f, true)} className="text-muted-foreground hover:text-primary p-1" data-testid="attachment-download" title="Download"><DownloadSimple size={15} /></button>
                {!readOnly && <button onClick={() => setToDelete(f)} className="text-muted-foreground hover:text-destructive p-1" data-testid="attachment-delete" title="Remove"><Trash size={15} /></button>}
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
        title="Remove attachment?" description={`"${toDelete?.original_filename}" will be removed from this document.`}
        confirmLabel="Remove" onConfirm={() => del.mutate(toDelete.id)} />
    </div>
  );
}
